import { useMemo } from 'react'
import {
  DocPageScope,
  DocPageSummaryFragment,
  useArchiveDocPageMutation,
  useUnarchiveDocPageMutation,
  useCreateDocPageMutation,
  useDeleteDocPageMutation,
  useDocPagesTreeQuery,
  usePublishDocPageMutation,
  useReorderDocPagesMutation,
  useSaveDocPageDraftMutation,
  useUpdateDocPageMutation,
} from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { buildDocTree } from '../utils/docs'

/** Loads the page tree for a scope and exposes the doc management mutations. */
export const useDocs = (scope: DocPageScope = DocPageScope.Org) => {
  const { data, loading, refetch } = useDocPagesTreeQuery({ ...GRAPHQL_QUERY_POLICY, variables: { scope } })

  const pages = useMemo(
    () => (data?.docPagesTree?.docPages || []).filter((p): p is DocPageSummaryFragment => !!p),
    [data],
  )
  const tree = useMemo(() => buildDocTree(pages), [pages])

  const [createDocPage, { loading: creating }] = useCreateDocPageMutation()
  const [updateDocPage] = useUpdateDocPageMutation()
  const [saveDraft] = useSaveDocPageDraftMutation()
  const [publishDocPage, { loading: publishing }] = usePublishDocPageMutation()
  const [archiveDocPage] = useArchiveDocPageMutation()
  const [unarchiveDocPage] = useUnarchiveDocPageMutation()
  const [deleteDocPage] = useDeleteDocPageMutation()
  const [reorderDocPages] = useReorderDocPagesMutation()

  return {
    tree,
    flatPages: pages,
    loading,
    refetch,
    createDocPage,
    creating,
    updateDocPage,
    saveDraft,
    publishDocPage,
    publishing,
    archiveDocPage,
    unarchiveDocPage,
    deleteDocPage,
    reorderDocPages,
  }
}
